import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

import { Container, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { TUTOR_PROFILE_SETTINGS_API } from "../../../api/api";
import { toast } from "react-toastify";

import { initialsFromName } from "../../../utils/getInitials";
import { handleApiError } from "../../../utils/apiErrorHandler";

import "./TutorProfile.css";

const TutorProfile = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    qualification: "",
    bio: "",
  });

  const [originalProfileData, setOriginalProfileData] = useState(profileData); // Track original profileData
  const [subjects, setSubjects] = useState([]);
  const [avatar, setAvatar] = useState(null);
  const [originalAvatar, setOriginalAvatar] = useState(null); // Track original avatar

  //loading
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => setAvatar(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleProfileCancel = () => {
    setProfileData(originalProfileData);
    setAvatar(originalAvatar);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      navigate("/signin");

      return;
    }

    const fetchProfile = async () => {
      try {
        const response = await axios.post(
          TUTOR_PROFILE_SETTINGS_API.TUTOR_GET_PROFILE,
          { user_id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200 && data.data) {
          const fetchedProfile = {
            fullName: data.data.full_name || "",
            email: data.data.email || "",
            phoneNumber: data.data.phone_number || "",
            qualification: data.data.degree || "",
            bio: data.data.professional_background || "",
          };

          setProfileData(fetchedProfile);
          setOriginalProfileData(fetchedProfile);

          // Set avatar from API
          if (data.data.profile_pic) {
            setAvatar(data.data.profile_pic);
            setOriginalAvatar(data.data.profile_pic);
          }
          if (data.data.subject_to_teach) {
            setSubjects([data.data.subject_to_teach]);
          }
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/signin");
      }
    };

    fetchProfile();
  }, [navigate]);

  const isProfileChanged = () => {
    // Check if form fields changed or avatar changed
    return (
      JSON.stringify(profileData) !== JSON.stringify(originalProfileData) ||
      avatar !== originalAvatar
    );
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setIsProfileLoading(false);
      navigate("/signin");

      return;
    }

    if (!profileData.fullName || !profileData.phoneNumber || !profileData.bio) {
      showErrorToast("Please fill all required fields");
      return;
    }

    // full Name only letters & spaces
    if (!/^[a-zA-Z ]+$/.test(profileData.fullName)) {
      showErrorToast("Full Name is required alphabets only.");
      return;
    }

    // Phone number: exactly 10 digits
    if (!/^[6-9]\d{9}$/.test(profileData.phoneNumber)) {
      showErrorToast("Enter a valid 10-digit phone number.");
      return;
    }

    // Minimum length validation for bio
    if (profileData.bio.length < 30) {
      showErrorToast("Professional Bio must be at least 30 characters.");
      return;
    }

    //  Message validation: must contain at least one alphabet
    if (!/[a-zA-Z]/.test(profileData.bio)) {
      showErrorToast("Professional Bio must contain at least one letter");
      return;
    }

    const formData = new FormData();
    formData.append("user_id", userId);
    // formData.append("full_name", profileData.fullName);
    // formData.append("phone_number", profileData.phoneNumber);
    formData.append("professional_background", profileData.bio);

    if (fileInputRef.current.files[0]) {
      formData.append("profile_pic", fileInputRef.current.files[0]);
    }

    setIsProfileLoading(true);
    try {
      const response = await axios.post(
        TUTOR_PROFILE_SETTINGS_API.TUTOR_UPDATE_PROFILE,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        setOriginalProfileData(profileData);
        setOriginalAvatar(avatar);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setIsProfileLoading(false);
    }
  };

  return (
    <Container className="tutor-profile-container">
      <div className="tutor-profile-card">
        {/* HEADER */}
        <h5 className="tutor-profile-title">Personal Details</h5>
        <p className="tutor-profile-subtitle">
          Keep your profile information accurate and up to date.
        </p>

        {/* Avatar Section */}
        <div className="tutor-profile-avatar-row">
          <div className="tutor-profile-avatar">
            {avatar ? (
              <img src={avatar} alt="Avatar" className="avatar-img" />
            ) : (
              initialsFromName(profileData.fullName)
            )}
          </div>

          <div className="tutor-profile-avatar-actions">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              style={{ display: "none" }}
              onChange={handlePhotoUpload}
            />
            <Button
              className="tutor-profile-change-photo-btn"
              onClick={() => fileInputRef.current.click()}
            >
              Change Photo
            </Button>
            <p className="tutor-profile-photo-hint">
              JPG, PNG or GIF (max 5MB)
            </p>
          </div>
        </div>

        {/* FORM FIELDS */}
        <Form className="tutor-profile-form" onSubmit={handleProfileUpdate}>
          <Form.Group className="tutor-profile-form-group">
            <Form.Label className="tutor-profile-form-label">
              Full Name *
            </Form.Label>
            <Form.Control
              type="text"
              name="fullName"
              placeholder="Enter your full name"
              value={profileData.fullName}
              onChange={handleProfileChange}
              className={`tutor-profile-input ${
                profileData.fullName ? "filled" : ""
              }`}
              readOnly
            />
          </Form.Group>

          <Form.Group className="tutor-profile-form-group">
            <Form.Label className="tutor-profile-form-label">Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              placeholder="Enter your email"
              value={profileData.email}
              onChange={handleProfileChange}
              className={`tutor-profile-input ${
                profileData.email ? "filled" : ""
              }`}
              readOnly
            />
          </Form.Group>

          <Form.Group className="tutor-profile-form-group">
            <Form.Label className="tutor-profile-form-label">
              Phone Number
            </Form.Label>
            <Form.Control
              type="text"
              name="phoneNumber"
              placeholder="Enter your phone number"
              value={profileData.phoneNumber}
              onChange={handleProfileChange}
              className={`tutor-profile-input ${
                profileData.phoneNumber ? "filled" : ""
              }`}
              readOnly
            />
          </Form.Group>

          <Form.Group className="tutor-profile-form-group">
            <Form.Label className="tutor-profile-form-label">
              Highest Qualification
            </Form.Label>
            <Form.Control
              type="text"
              name="qualification"
              placeholder="Enter your highest qualification"
              value={profileData.qualification}
              // onChange={handleProfileChange}
              disabled
              className={`tutor-profile-input ${
                profileData.qualification ? "filled" : ""
              }`}
            />
          </Form.Group>

          <Form.Group className="tutor-profile-form-group">
            <Form.Label className="tutor-profile-form-label">
              Professional Bio
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="bio"
              placeholder="Write a brief professional bio"
              value={profileData.bio}
              onChange={handleProfileChange}
              className={`tutor-profile-textarea ${
                profileData.bio ? "filled" : ""
              }`}
            />
          </Form.Group>

          {/* Teaching Expertise */}
          <Form.Group className="tutor-profile-form-group">
            <Form.Label className="tutor-profile-form-label">
              Teaching Expertise
            </Form.Label>

            <div className="tutor-profile-tags-row">
              {subjects.length > 0 ? (
                subjects.map((sub, i) => (
                  <span key={i} className="tutor-profile-tag">
                    {sub}
                  </span>
                ))
              ) : (
                <span className="tutor-profile-tag empty">
                  No subject added
                </span>
              )}

              <button
                type="button"
                className="tutor-profile-add-course-btn"
                onClick={() => navigate("/header/tutor/my-courses")}
              >
                + Add Course
              </button>
            </div>
          </Form.Group>

          {/* Footer Buttons */}
          <div className="tutor-profile-footer">
            {isProfileChanged() && (
              <Button
                type="button"
                className="tutor-profile-cancel-btn"
                onClick={handleProfileCancel}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              className="tutor-profile-save-btn"
              disabled={!isProfileChanged() || isProfileLoading}
            >
              {isProfileLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </Form>
      </div>
    </Container>
  );
};

export default TutorProfile;
